import PBService from "@irati/utils/services"
import client, {MutationOptions, QueryOptions} from "@irati/utils/services/apollo"
import {
  CreateDealBrandInput,
  DealBrand,
  DealBrandStatus,
  QueryDealBrandsArgs,
  QueryDealBrandBySlugArgs,
  UpdateDealBrandInput
} from "@irati/types"
import listFormat, {ListFormatArray} from "@irati/utils/services/graph/utils/listFormat"

import dealBrandQuery, {dealBrandKey} from "./queries/dealBrand"
import dealBrandsQuery, {dealBrandsKey} from "./queries/dealBrands"
import dealBrandBySlugQuery, {dealBrandBySlugKey} from "./queries/dealBrandBySlug"
import createDealBrandMutation, {createDealBrandKey} from "./mutations/createDealBrand"
import updateDealBrandMutation, {updateDealBrandKey} from "./mutations/updateDealBrand"
import dealBrandDedupMutation, {dealBrandDedupKey} from "./mutations/dealBrandDedup"

import dealBrandsErrorHandler from "./errors/errorHandler"

class DealBrandService extends PBService {
  get = async ({uuid, withVariants = false}: {uuid: string; withVariants?: boolean}, options: QueryOptions = {}): Promise<DealBrand> => {
    const variables = {uuid, withVariants}
    const {data} = await client.query({...options, query: dealBrandQuery, variables})
    return data[dealBrandKey]
  }

  getBySlug = async (args: QueryDealBrandBySlugArgs, options: QueryOptions = {}): Promise<DealBrand> => {
    const variables = {
      ...args,
      withVariants: false
    }
    const {data} = await client.query({...options, query: dealBrandBySlugQuery, variables})
    return data[dealBrandBySlugKey]
  }

  list = async (args?: QueryDealBrandsArgs, options: QueryOptions = {}): Promise<ListFormatArray<DealBrand>> => {
    const variables = {...args}
    const {data} = await client.query({...options, query: dealBrandsQuery, variables})
    return listFormat(data[dealBrandsKey])
  }

  create = async (args: CreateDealBrandInput, options: MutationOptions = {}): Promise<DealBrand> => {
    const variables = {
      withVariants: false,
      params: {...args}
    }
    const {data} = await client.mutate({...options, mutation: createDealBrandMutation, variables})
    return data[createDealBrandKey]
  }

  update = async (args: UpdateDealBrandInput, options: MutationOptions = {}): Promise<DealBrand> => {
    const {approved, bannerUrl, description, label, logoUrl, parentId, status, uuid, slug} = args

    const variables = {
      params: {
        approved,
        bannerUrl,
        description,
        label,
        logoUrl,
        parentId: parentId || "",
        status,
        uuid,
        slug
      },
      withVariants: true
    }
    const {data} = await client.mutate({...options, mutation: updateDealBrandMutation, variables})
    return data[updateDealBrandKey]
  }

  rejectBrand = (dealBrandId: string) => {
    const args = {
      uuid: dealBrandId,
      status: DealBrandStatus.StatusOff,
      parentId: ""
    }
    return this.update(args)
  }

  removeVariant = async (variantId: string) => {
    const args = {
      uuid: variantId,
      parentId: "",
      status: DealBrandStatus.StatusOn,
      approved: false
    }
    return this.update(args)
  }

  dedup = async (uuid: string, options: QueryOptions = {}) => {
    const {data} = await client.mutate({...options, mutation: dealBrandDedupMutation, variables: {uuid}})
    return data[dealBrandDedupKey]
  }
}

const dealBrandService = new DealBrandService({errorHandlers: [dealBrandsErrorHandler]})

export default dealBrandService
